// Dashboard (the xenquote console's layout): a greeting, numbered "Start here" setup
// tasks, then the headline numbers from GET /v1/account (credits, buildings held,
// packs in progress, jobs won), and once the company has ordered, its buildings on a map.
import { useEffect, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { Map, Search, UserRound } from "lucide-react";
import { useApi, type AccountSummary, type CompanyProfile, type OwnedBuilding } from "../api";
import { useAuth } from "../auth/AuthContext";
import { Attention } from "../components/Attention";
import { BuildingsMap } from "../components/BuildingsMap";
import "../dashboard.css";
import "../my-buildings.css";

// One setup task on the dashboard's Start here list
type Task = { key: string; title: string; body: string; to: string; action: string; icon: ReactNode };

/** Credits can be fractional (1.5 for a mid-size pack): show "3.5", not "3.50". */
const credits = (n: number) => (Number.isInteger(n) ? String(n) : n.toFixed(1));

export function Dashboard() {
  const { user } = useAuth();
  const api = useApi();

  // Headline numbers; null = still loading
  const [account, setAccount] = useState<AccountSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    api
      .getAccount()
      // Guard against a malformed body (e.g. no API configured locally)
      .then((a) => setAccount(a && a.credits && a.buildings ? a : null))
      .catch((e: Error) => setError(e.message));
  }, [api]);

  // Account → Profile: for the greeting and the "complete your profile" task
  const [profile, setProfile] = useState<CompanyProfile | null>(null);
  useEffect(() => {
    api
      .getProfile()
      .then(setProfile)
      .catch(() => undefined);
  }, [api]);
  // Once the company has ordered: its buildings and districts for the map
  const hasBuildings = Boolean(account && (account.buildings.held > 0 || account.buildings.packsInProgress > 0));
  const [mine, setMine] = useState<{ buildings: OwnedBuilding[]; districts: string[] } | null>(null);
  useEffect(() => {
    if (!hasBuildings) return;
    Promise.all([api.listMyBuildings(), api.getCoverage()])
      .then(([buildings, cov]) => setMine({ buildings, districts: cov.coverage.councils }))
      .catch(() => undefined);
  }, [api, hasBuildings]);

  // Profile first name, else the SSO (Google) first name from the token, else the email
  const firstName = profile?.user.firstName.trim() || user?.givenName || null;
  const greetName = firstName ?? user?.email;

  // Setup tasks still to do, in order (only shown once we know)
  const tasks: Task[] = [];
  if (profile && (!firstName || !profile.company.companyName.trim())) {
    tasks.push({
      key: "profile",
      title: "Complete your profile",
      body: "Add your name and your company's details. Your company name goes on every report we prepare for you.",
      to: "/profile",
      action: "Complete profile",
      icon: <UserRound size={16} aria-hidden="true" />,
    });
  }
  // Until the Local Authority Districts are saved (and locked): once set, this task goes
  if (account && !account.coverageSet) tasks.push({
    key: "coverage",
    title: "Set your coverage",
    body: "Choose the Local Authority Districts you work in. Your shortlist only shows properties there; then set which ones you want in Filters.",
    to: "/coverage",
    action: "Set coverage",
    icon: <Map size={16} aria-hidden="true" />,
  });
  // Until the company has ordered a pack (then the task is done)
  if (account && account.buildings.held === 0 && account.buildings.packsInProgress === 0) tasks.push({
    key: "find",
    title: "Find your first property",
    body: "Browse properties that use a lot of electricity and have no solar, then order a report: it's yours exclusively for 6 months.",
    to: "/find",
    action: "Search properties",
    icon: <Search size={16} aria-hidden="true" />,
  });

  const kpis: { label: string; value: string; note: string }[] = [
    {
      label: "Report credits",
      value: account ? credits(account.credits.balance) : "—",
      note: account?.plan ? `${credits(account.plan.creditsPerMonth)} a month on ${account.plan.name}` : "No plan yet",
    },
    { label: "Properties held", value: account ? String(account.buildings.held) : "—", note: "Exclusive to you for 6 months" },
    { label: "Reports in progress", value: account ? String(account.buildings.packsInProgress) : "—", note: "Usually 2 working days" },
    { label: "Jobs won", value: account ? String(account.buildings.won) : "—", note: "From your reports" },
  ];

  return (
    <div className="page">
      <div className="page-head">
        <h1>Dashboard</h1>
        <p>
          Welcome{greetName ? `, ${greetName}` : ""}.
          {account?.company.companyName ? ` Here's where ${account.company.companyName} stands.` : " Here's where you stand."}
        </p>
      </div>

      {/* Start here: numbered setup tasks; the pulsing star marks the next one */}
      {tasks.length > 0 && (
        <section className="start-tasks" aria-label="Start here">
          <div className="start-here-label">Start here</div>
          <ol>
            {tasks.map((t, i) => (
              <li key={t.key} className="start-task">
                <div className="card start-here">
                  <span className="start-task-num has-attention">
                    {i + 1}
                    {i === 0 && <Attention corner="tr" label="Do this first" />}
                  </span>
                  <div className="start-task-text">
                    <h2>{t.title}</h2>
                    <p className="card-sub">{t.body}</p>
                  </div>
                  <Link to={t.to} className={`btn ${i === 0 ? "btn-primary" : "btn-secondary"}`}>
                    {t.icon} {t.action}
                  </Link>
                </div>
              </li>
            ))}
          </ol>
        </section>
      )}

      {error && <div className="flash flash-danger">{error}</div>}
      <section className="kpi-row" aria-label="Headline numbers">
        {kpis.map((k, i) => (
          <div key={k.label} className="card kpi" style={{ ["--i" as string]: i }}>
            <div className="kpi-label">{k.label}</div>
            <div className="kpi-value">{k.value}</div>
            <div className="kpi-note">{k.note}</div>
          </div>
        ))}
      </section>

      {/* Your properties on the map: hover a pin for its details */}
      {mine && mine.buildings.length > 0 && (
        <section className="card dmap" aria-label="Your properties on the map">
          <div className="dmap-head">
            <h2>Your properties</h2>
            <Link to="/properties">All my properties →</Link>
          </div>
          <BuildingsMap buildings={mine.buildings} districts={mine.districts} />
        </section>
      )}
    </div>
  );
}
